import { type ReactNode, useMemo } from 'react';
import {
  type RaRecord,
  useGetResourceLabel,
  useRecordContext,
  useResourceContext,
  useTranslate,
} from 'ra-core';
import { Edit, type EditProps } from '../../components/admin/edit';
import { MoonstarDeleteButton } from './MoonstarDeleteButton';
import { MoonstarTitle } from './MoonstarTitle';

export type MoonstarEditProps<TRecord extends RaRecord = RaRecord> = Omit<
  EditProps,
  'disableBreadcrumb'
> & {
  children: ReactNode;
  hideDelete?: boolean | ((record: TRecord) => boolean);
  onDeleteRedirect?: () => string;
};

/**
 * Kit `Edit` (pessimistic by default) with the `[resource label, "Edit"]` breadcrumb in the header,
 * and the page `actions` followed by a delete button (with confirmation) next to the title.
 */
export function MoonstarEdit<TRecord extends RaRecord = RaRecord>({
  children,
  actions,
  hideDelete,
  onDeleteRedirect,
  ...props
}: MoonstarEditProps<TRecord>) {
  const resource = useResourceContext(props);
  const getLabel = useGetResourceLabel();
  const translate = useTranslate();

  const label = getLabel(resource!, 1);
  const edit = translate('ra.action.edit');

  return (
    <>
      <MoonstarTitle breadcrumb={[label, edit]} />
      <Edit
        mutationMode="pessimistic"
        {...props}
        disableBreadcrumb
        actions={
          <div className="flex items-center justify-end gap-2">
            {actions}
            <EditDeleteButton
              hideDelete={hideDelete}
              onDeleteRedirect={onDeleteRedirect}
            />
          </div>
        }
      >
        {children}
      </Edit>
    </>
  );
}

function EditDeleteButton<TRecord extends RaRecord = RaRecord>({
  hideDelete,
  onDeleteRedirect,
}: Pick<MoonstarEditProps<TRecord>, 'hideDelete' | 'onDeleteRedirect'>) {
  const record = useRecordContext<TRecord>();

  const hide = useMemo(() => {
    if (typeof hideDelete === 'function') {
      if (record === undefined) return true;
      return hideDelete(record);
    }

    return hideDelete;
  }, [hideDelete, record]);

  if (hide) {
    return null;
  }

  return <MoonstarDeleteButton redirect={onDeleteRedirect} />;
}
